import React, { useEffect, useState } from "react";
import { ThemeProvider } from "@/components/shared/ThemeProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ChatProvider } from "@/components/layouts/Chat/ChatContext";
import { ReportRunsProvider } from "@/components/global/Chat/components/dreamerReports/ReportRunsContext";
import { MessagingProvider } from "@/entities/messaging/MessagingContext";
import { Route, Routes } from "react-router-dom";
import useI18n from "@/services/translation/hook";
import LoginErrorPage from "@/components/layouts/FullpageMessage/LoginErrorPage";
import Settings from "@/pages/settings/Settings";
import * as Sentry from "@sentry/react";
import { setupFeatureFlags } from "@/services/features/setup";
import { SidebarProvider } from "@/services/context/SidebarContext";
import { ArrMrrProvider } from "@/services/context/ArrMrrContext";
import MainNavigationAndAuth from "@/components/MainNavigationAndAuth";
import { useVersionMonitor } from "@/hooks/useVersionMonitor";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const DarkModeContext = React.createContext({
  toggleDarkMode: () => {},
  darkMode: false
});

function App() {
  const [translationsLoaded, setTranslationsLoaded] = useState(false);
  const i18n = useI18n();

  useVersionMonitor();

  useEffect(() => {
    // setupFeatureFlags rethrows an initialisation failure, and nothing awaits it
// here — a Flagsmith outage would otherwise surface as an unhandled rejection.
// Flags fall back to their defaults, which for the rollout means off.
setupFeatureFlags().catch((error) =>
  console.error("Feature flag initialisation failed:", error)
);
  }, []);

  useEffect(() => {
    const handleLoaded = () => {
      setTranslationsLoaded(true);
    };

    i18n.on("loaded", handleLoaded);

    if (i18n.isInitialized) {
      setTranslationsLoaded(true);
    }

    return () => {
      i18n.off("loaded", handleLoaded);
    };
  }, [i18n]);

  return (
    <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
      <TooltipProvider delayDuration={400}>
        <ArrMrrProvider>
          <MessagingProvider>
            <ChatProvider>
              <ReportRunsProvider>
                <SidebarProvider>
                  {translationsLoaded && (
                    <SentryRoutes>
                      <Route
                        path="/loginErrorPage"
                        element={<LoginErrorPage />}
                      />
                      <Route path="/settings/*" element={<Settings />} />
                      <Route path="/*" element={<MainNavigationAndAuth />} />
                    </SentryRoutes>
                  )}
                </SidebarProvider>
              </ReportRunsProvider>
            </ChatProvider>
          </MessagingProvider>
        </ArrMrrProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}

export default App;
